import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Search, X } from 'lucide-react';
import ResponsiveNavigation from '../../components/ResponsiveNavigation';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [filters, setFilters] = useState({ username: '', role: '' });
  const [loading, setLoading] = useState(false);
  const [updatingUserId, setUpdatingUserId] = useState(null);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async (searchFilters = filters) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchFilters.username) params.append('username', searchFilters.username);
      if (searchFilters.role) params.append('role', searchFilters.role);

      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/users?${params.toString()}`
      );
      
      if (response.data.success) {
        // Sort by role rank: JADM → CADM → CREC → PROS (unknowns last)
        const rank = {
          jobasti_admin:    0,
          client_admin:     1,
          client_recruiter: 2,
          prospect:         3,
        };
        const sorted = [...response.data.data].sort((a, b) => {
          const ra = rank[a.role] ?? 99;
          const rb = rank[b.role] ?? 99;
          if (ra !== rb) return ra - rb;
          // secondary sort by username / email so order is stable inside a role
          return String(a.username || a.email || '').localeCompare(String(b.username || b.email || ''));
        });
        setUsers(sorted);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    fetchUsers();
  };

  const handleReset = () => {
    setFilters({ username: '', role: '' });
    fetchUsers({ username: '', role: '' });
  };

  const handleRoleChange = async (userId, newRole, currentRole) => {
    // Only allow changes between client_recruiter and client_admin
    const allowedRoles = ['client_recruiter', 'client_admin'];
    if (!allowedRoles.includes(currentRole) || !allowedRoles.includes(newRole)) {
      alert('Role can only be changed between Client Recruiter and Client Admin');
      return;
    }

    setUpdatingUserId(userId);
    try {
      const response = await axios.patch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/users/${userId}/role`,
        { role: newRole }
      );

      if (response.data.success) {
        // Update local state
        setUsers(users.map(user => 
          user.id === userId ? { ...user, role: newRole } : user
        ));
        alert('User role updated successfully!');
      }
    } catch (error) {
      console.error('Error updating role:', error);
      alert(error.response?.data?.message || 'Failed to update user role');
    } finally {
      setUpdatingUserId(null);
    }
  };

  const getRoleDisplayName = (role) => {
    const roleMap = {
      'jobasti_admin': 'JobastiIQ Admin',
      'jobasti_recruiter': 'JobastiIQ Recruiter',
      'client_admin': 'Client Admin',
      'client_recruiter': 'Client Recruiter',
      'prospect': 'Prospect'
    };
    return roleMap[role] || role;
  };

  const getRoleAbbreviation = (role) => {
    const roleMap = {
      'jobasti_admin': 'JADM',
      'jobasti_recruiter': 'JREC',
      'client_admin': 'CADM',
      'client_recruiter': 'CREC',
      'prospect': 'PROS'
    };
    return roleMap[role] || role;
  };

  const canChangeRole = (role) => {
    return ['client_recruiter', 'client_admin'].includes(role);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <ResponsiveNavigation />
      
      <div className="p-6">
        <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Users</h1>
          <button className="text-blue-600 hover:text-blue-700 font-medium">
            + add user
          </button>
        </div>

        {/* Search Filters — pill-style search bar (matches recruiter MyCandidates) */}
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row items-stretch bg-white rounded-full shadow-sm border-2 border-gray-900/90 focus-within:border-blue-500 transition-colors overflow-hidden">
            <div className="flex items-center gap-2 flex-1 min-w-0 px-5 py-3.5 border-r border-gray-200">
              <Search className="w-4 h-4 text-gray-500 flex-shrink-0" />
              <input
                type="text"
                placeholder="Username..."
                value={filters.username}
                onChange={(e) => setFilters({ ...filters, username: e.target.value })}
                onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
                className="flex-1 bg-transparent outline-none text-sm text-gray-900 placeholder:text-gray-400 min-w-0"
                data-testid="filter-username"
              />
              {filters.username && (
                <button type="button" onClick={() => setFilters({ ...filters, username: '' })} className="p-1 text-gray-400 hover:text-gray-600" aria-label="Clear username">
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            <div className="flex items-center gap-2 flex-1 min-w-0 px-3 py-1.5 relative">
              <Select
                value={filters.role || 'all'}
                onValueChange={(v) => {
                  const next = { ...filters, role: v === 'all' ? '' : v };
                  setFilters(next);
                  fetchUsers(next);
                }}
              >
                <SelectTrigger
                  data-testid="filter-role"
                  className="flex-1 border-0 shadow-none bg-transparent h-9 px-2 focus:ring-0 focus:ring-offset-0 text-sm text-gray-900 data-[placeholder]:text-gray-400"
                >
                  <SelectValue placeholder="All roles" />
                </SelectTrigger>
                <SelectContent className="rounded-xl shadow-lg border border-gray-200 min-w-[220px]">
                  <SelectItem value="all">All roles</SelectItem>
                  <SelectItem value="jobasti_admin">Jobasti Super Admin</SelectItem>
                  <SelectItem value="client_admin">Client Admin</SelectItem>
                  <SelectItem value="client_recruiter">Recruiter</SelectItem>
                  <SelectItem value="prospect">Prospect</SelectItem>
                </SelectContent>
              </Select>
              {filters.role && (
                <button
                  type="button"
                  onClick={() => { const next = { ...filters, role: '' }; setFilters(next); fetchUsers(next); }}
                  className="p-1 text-gray-400 hover:text-gray-600 flex-shrink-0"
                  aria-label="Clear role"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            <button
              onClick={handleReset}
              className="bg-blue-500 hover:bg-blue-600 text-white font-semibold text-sm px-8 py-3.5 sm:m-2 rounded-full transition-colors"
              data-testid="reset-filter-btn"
            >
              Reset filters
            </button>
          </div>
        </div>

        {/* Users Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Username
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Platform role
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="2" className="px-6 py-4 text-center text-gray-500">
                    Loading...
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan="2" className="px-6 py-4 text-center text-gray-500">
                    No users found
                  </td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{user.username}</div>
                      <div className="text-xs text-gray-500">{user.email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {canChangeRole(user.role) ? (
                        <select
                          value={user.role}
                          onChange={(e) => handleRoleChange(user.id, e.target.value, user.role)}
                          disabled={updatingUserId === user.id}
                          className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                        >
                          <option value="client_recruiter">Client Recruiter (CREC)</option>
                          <option value="client_admin">Client Admin (CADM)</option>
                        </select>
                      ) : (
                        <span className="inline-flex items-center px-3 py-1 rounded-md text-sm font-medium bg-gray-100 text-gray-800">
                          {getRoleAbbreviation(user.role)}
                        </span>
                      )}
                      {updatingUserId === user.id && (
                        <span className="ml-2 text-xs text-gray-500">Updating...</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Role Legend */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-blue-900 mb-2">Role Information</h3>
          <div className="text-xs text-blue-800 space-y-1">
            <p><strong>JADM</strong> = JobastiIQ Admin (Super Admin) - Cannot be changed</p>
            <p><strong>JREC</strong> = JobastiIQ Recruiter (Agency) - Cannot be changed</p>
            <p><strong>CADM</strong> = Client Admin - Can be changed to Client Recruiter</p>
            <p><strong>CREC</strong> = Client Recruiter - Can be changed to Client Admin</p>
            <p><strong>PROS</strong> = Prospect (Candidate) - Cannot be changed</p>
          </div>
        </div>
        </div>
      </div>
    </div>
  );
};

export default AdminUsers;
